import React, { useState, useEffect, useCallback } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Chip,
  IconButton,
  Divider,
  Paper,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PetsIcon from "@mui/icons-material/Pets";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BedIcon from "@mui/icons-material/Bed";
import BathtubIcon from "@mui/icons-material/Bathtub";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoginIcon from "@mui/icons-material/Login";
import ImageSlider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config/config";
import Logo from "../../assets/LogoNew2.png";
import { useTheme } from "../../context/ThemeContext";
import LocationPickerMap from "../../components/LocationPickerMap";
import { useFormik } from "formik";
import ApartmentIcon from "@mui/icons-material/Apartment";
import GroupsIcon from "@mui/icons-material/Groups";
import GavelIcon from "@mui/icons-material/Gavel";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EventIcon from "@mui/icons-material/Event";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  amenityList,
  downloadPublicPropertyReport,
  hasTerms,
  houseRules,
  leaseTerms,
  moveInCosts,
  moveInTotal,
} from "../../utils/publicPropertyTerms";
import PropertyRatingBadge from "../../components/PropertyRatingBadge";
import PropertyRulesPanel, {
  RulesBadge,
} from "../../components/PropertyRulesPanel";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";

const CustomArrow = ({ onClick, direction }) => (
  <div
    className={`custom-arrow ${direction}`}
    onClick={onClick}
    role="button"
  />
);
// Slider settings for property images
const sliderSettings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
  autoplay: true,
  autoplaySpeed: 4000,
  nextArrow: <CustomArrow direction="next" />,
  prevArrow: <CustomArrow direction="prev" />,
};

// Default filter state
const defaultFilters = {
  city: "",
  area: "",
  rentRange: [0, 100000],
  bedrooms: "",
  allowedFor: "",
  petsAllowed: false,
};

export default function PublicSearchProperty() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();

  // States
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceAnchorEl, setPriceAnchorEl] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedLandlord, setSelectedLandlord] = useState({});
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Initialize filters from URL params
  const [filters, setFilters] = useState({
    city: searchParams.get("city") || "",
    area: searchParams.get("area") || "",
    rentRange: [
      Number(searchParams.get("minRent")) || 0,
      Number(searchParams.get("maxRent")) || 100000,
    ],
    bedrooms: searchParams.get("bedrooms") || "",
    allowedFor: searchParams.get("allowedFor") || "",
    petsAllowed: searchParams.get("petsAllowed") === "true",
    propertyAddress: searchParams.get("propertyAddress") || "",
  });
  const formik = useFormik({
    initialValues: {
      flatName: "",
      flatNumber: "",
      flatFloor: "",
      flatRooms: "",
      flatToilet: "",
      flatKitchen: "",
      flatRent: "",
      flatAdvance: "",
      flatMaintananceCharges: "",
      flatTrashCharges: "",
      personAllowed: "",
      scPerMonth: false,
      flatSecurityCharges: "",
      petAllowed: false,
      parkingAvailable: false,
      furnished: false,
      smokeAlarm: false,
      firstAidKit: false,
      fireExtinguisher: false,
      pool: false,
      bathTub: false,
      elevator: false,
      wifi: false,
      balcony: false,
      gym: false,
      roof: false,
      country: "Pakistan",
      city: "Karachi",
      images: [],
      previews: [],
      lateChargesPerDay: "",
      rentRelaxationDays: "",
      propertyAddress: "",
      lat: "",
      lng: "",
      mapAddress: "",
      note: "",
      is_rent: 0,
      is_active: true,
      tags: [],
      allowedFor: "family",
      landlordConditions: [],
      customConditions: "",
      rentDueDate: 5,
      noticePeriod: "1_month",
      rentIncrease: 10,
      leaseDuration: "11_months",
      // bill_uploaded_by: "landlord"
    },
  });
  // Fetch properties
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      console.log(filters, "filters");

      const params = new URLSearchParams();
      params.append("availability", "available");

      if (filters.city) params.append("city", filters.city);
      if (filters.propertyAddress)
        params.append(
          "q",
          filters.propertyAddress.split(" ")[0]?.replace(/[^a-zA-Z0-9]/g, "")
        );
      if (filters.area) params.append("area", filters.area);
      if (filters.rentRange[0] > 0)
        params.append("minRent", filters.rentRange[0]);
      if (filters.rentRange[1] < 100000)
        params.append("maxRent", filters.rentRange[1]);
      if (filters.bedrooms) params.append("bedrooms", filters.bedrooms);
      if (filters.allowedFor) params.append("allowedFor", filters.allowedFor);
      if (filters.petsAllowed) params.append("petsAllowed", "true");

      const response = await axios.get(
        `${BASE_URL}/find-public-property?${params.toString()}`
      );
      setProperties(response.data || []);
    } catch (error) {
      setProperties([]);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      propertyAddress: formik.values.propertyAddress || "",
    }));
    const params = new URLSearchParams();
    if (filters.propertyAddress)
      params.set("propertyAddress", formik.values.propertyAddress);
  }, [formik.values.propertyAddress]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Every inline control calls this directly — a click/selection IS the
  // search, same as the old "Apply" button used to trigger, just without the
  // drawer detour. Keeps the URL params (and therefore shareable/bookmarkable
  // searches) in sync exactly like handleApplyFilters used to.
  const updateFilters = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);

    const params = new URLSearchParams();
    if (next.allowedFor) params.set("allowedFor", next.allowedFor);
    if (next.bedrooms) params.set("bedrooms", next.bedrooms);
    if (next.rentRange[0] > 0) params.set("minRent", next.rentRange[0]);
    if (next.rentRange[1] < 100000) params.set("maxRent", next.rentRange[1]);
    if (next.petsAllowed) params.set("petsAllowed", "true");
    setSearchParams(params);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setSearchParams({});
  };

  // Open property detail modal. The owner's name/email sit on the search
  // result wrapper rather than the flat, so they have to be carried across for
  // the report to be able to name who listed it.
  const handleViewDetails = (property, landlord = {}) => {
    setSelectedProperty(property);
    setSelectedLandlord(landlord);
    setDetailModalOpen(true);
  };

  // Navigate to login to apply
  const handleApplyNow = (property) => {
    navigate("/login", {
      state: {
        type: "Tenant",
        redirect: `/find-property`,
        propertyId: property?._id,
      },
    });
  };

  // Count active filters
  const activeFilterCount = [
    filters.city,
    filters.allowedFor,
    filters.bedrooms,
    filters.rentRange[0] > 0 || filters.rentRange[1] < 100000,
    filters.petsAllowed,
  ].filter(Boolean).length;

  return (
    <Box sx={{ bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      {/* ============ NAVBAR ============ */}
      <AppBar
        position="sticky"
        sx={{ bgcolor: "white", boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => navigate("/")}
                sx={{ color: "#11998e" }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              >
                {/* <Box component="img" src={Logo1} alt="OTS Logo" sx={{ height: 50, mr: 1 }} /> */}
                <Box
                  sx={{
                    height: 50,
                    width: 50,
                    // mr: 1.5,
                    display: "inline-block",
                    backgroundColor: theme.primary,

                    maskImage: `url(${Logo})`,
                    WebkitMaskImage: `url(${Logo})`,
                    maskRepeat: "no-repeat",
                    WebkitMaskRepeat: "no-repeat",

                    maskSize: "120%",
                    WebkitMaskSize: "120%",

                    maskPosition: "center",
                    WebkitMaskPosition: "center",
                  }}
                />
                <Box
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    flexDirection: "column",
                    lineHeight: 0.95,
                  }}
                >
                  {/* Owner Tenant */}
                  <Typography
                    sx={{
                      // ...goldGradient,
                      background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                      // "linear-gradient(90deg, #c9a35c 0%, #e6c27a 50%, #a07b3a 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontSize: "20px",
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                    }}
                  >
                    Owner Tenant
                  </Typography>

                  {/* SOLUTION */}
                  <Typography
                    sx={{
                      // ...goldGradient,
                      background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                      // "linear-gradient(90deg, #c9a35c 0%, #e6c27a 50%, #a07b3a 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontSize: "18px",
                      fontWeight: 500,
                      letterSpacing: "0.40em",
                      mt: "-5px",
                    }}
                  >
                    SOLUTION
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<LoginIcon />}
                sx={{ borderColor: "#11998e", color: "#11998e" }}
                onClick={() =>
                  navigate("/login", { state: { type: "Tenant" } })
                }
              >
                Login
              </Button>
              <Button
                variant="contained"
                sx={{ bgcolor: "#11998e", "&:hover": { bgcolor: "#0d7a6e" } }}
                onClick={() =>
                  navigate("/login", { state: { type: "Landlord" } })
                }
              >
                List Property
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ============ SEARCH BAR ============
          Every control below writes straight into `filters` (via
          updateFilters), so picking an option IS the search — no drawer,
          no draft state, no separate "Apply" click. Same fetchProperties
          call as before, same URL-param sync as the old Apply button. */}
      <Box sx={{ bgcolor: "white", py: 2, borderBottom: "1px solid #e0e0e0" }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <LocationPickerMap formik={formik} isHero={true} />
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Bedrooms</InputLabel>
                <Select
                  value={filters.bedrooms}
                  label="Bedrooms"
                  onChange={(e) => updateFilters({ bedrooms: e.target.value })}
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="1">1</MenuItem>
                  <MenuItem value="2">2</MenuItem>
                  <MenuItem value="3">3</MenuItem>
                  <MenuItem value="4">4</MenuItem>
                  <MenuItem value="4+">4+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tenant Type</InputLabel>
                <Select
                  value={filters.allowedFor}
                  label="Tenant Type"
                  onChange={(e) =>
                    updateFilters({ allowedFor: e.target.value })
                  }
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="family">Family</MenuItem>
                  <MenuItem value="bachelor">Bachelor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={fetchProperties}
                sx={{ bgcolor: "#11998e", "&:hover": { bgcolor: "#0d7a6e" } }}
              >
                Search
              </Button>
            </Grid>
          </Grid>

          {/* Quick filter chips - Price Range and Pets Allowed were previously
              only reachable via the drawer; now they're one click away. */}
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ mt: 2, overflowX: "auto", pb: 0.5 }}
          >
            <Chip
              clickable
              variant={
                filters.rentRange[0] > 0 || filters.rentRange[1] < 100000
                  ? "filled"
                  : "outlined"
              }
              onClick={(e) => setPriceAnchorEl(e.currentTarget)}
              label={
                filters.rentRange[0] > 0 || filters.rentRange[1] < 100000
                  ? `Rs ${filters.rentRange[0].toLocaleString()} - ${filters.rentRange[1].toLocaleString()}`
                  : "Price Range"
              }
              sx={{
                flexShrink: 0,
                height: 36,
                ...(filters.rentRange[0] > 0 || filters.rentRange[1] < 100000
                  ? { bgcolor: "#11998e", color: "#fff" }
                  : {}),
              }}
            />
            <Popover
              open={Boolean(priceAnchorEl)}
              anchorEl={priceAnchorEl}
              onClose={() => setPriceAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Box sx={{ p: 3, width: 280 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Rent Range (Rs.)
                </Typography>
                <Slider
                  value={filters.rentRange}
                  // Committed (not live) - the fetch here has no debounce,
                  // so it should fire once per drag, same as Apply used to.
                  onChange={(e, newValue) =>
                    setFilters((prev) => ({ ...prev, rentRange: newValue }))
                  }
                  onChangeCommitted={(e, newValue) =>
                    updateFilters({ rentRange: newValue })
                  }
                  valueLabelDisplay="auto"
                  min={0}
                  max={100000}
                  step={5000}
                  sx={{ color: "#11998e" }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" color="text.secondary">
                    Rs. {filters.rentRange[0].toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Rs. {filters.rentRange[1].toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Popover>

            <Chip
              clickable
              icon={<PetsIcon />}
              label="Pets Allowed"
              variant={filters.petsAllowed ? "filled" : "outlined"}
              onClick={() =>
                updateFilters({ petsAllowed: !filters.petsAllowed })
              }
              sx={{
                flexShrink: 0,
                height: 36,
                ...(filters.petsAllowed
                  ? { bgcolor: "#11998e", color: "#fff" }
                  : {}),
              }}
            />

            {activeFilterCount > 0 && (
              <Chip
                clickable
                icon={<CloseIcon />}
                label="Clear All"
                variant="outlined"
                color="error"
                onClick={handleResetFilters}
                sx={{ flexShrink: 0, height: 36 }}
              />
            )}
          </Stack>
        </Container>
      </Box>

      {/* ============ RESULTS ============ */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Results Header — filter state (and clearing it) now lives in the
            chip bar above, right next to the controls that set it. */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={600} color="#1a1a2e">
            {loading ? "Searching..." : `${properties.length} Properties Found`}
          </Typography>
        </Box>

        {/* Property Grid */}
        <Grid container spacing={3}>
          {loading ? (
            // Skeleton loaders
            [...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ borderRadius: 3 }}>
                  <Skeleton variant="rectangular" height={200} />
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="text" width="60%" height={30} />
                    <Skeleton variant="text" width="40%" />
                    <Skeleton variant="text" width="80%" />
                  </Box>
                </Card>
              </Grid>
            ))
          ) : properties.length === 0 ? (
            // No results
            <Grid item xs={12}>
              <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
                <HomeIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No properties found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Try adjusting your filters or search in a different city
                </Typography>
                <Button variant="outlined" onClick={handleResetFilters}>
                  Reset Filters
                </Button>
              </Paper>
            </Grid>
          ) : (
            // Property cards
            properties.map((item) => {
              const flat = item.property || item.flats?.[0] || item;
              const images = flat.flatImages || flat.images || [];

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={flat._id || item._id}
                  sx={{
                    display: "flex",
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      overflow: "hidden",
                      transition: "all 0.3s ease",

                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
                      },
                    }}
                  >
                    {/* Image Slider */}
                    <Box sx={{ position: "relative", height: 200 }}>
                      {images.length > 0 ? (
                        <ImageSlider {...sliderSettings}>
                          {images.map((img, idx) => (
                            <Box key={idx} sx={{ height: 200 }}>
                              <CardMedia
                                component="img"
                                height="200"
                                image={img}
                                alt={`Property ${idx + 1}`}
                                sx={{ objectFit: "cover" }}
                              />
                            </Box>
                          ))}
                        </ImageSlider>
                      ) : (
                        <Box
                          sx={{
                            height: 200,
                            bgcolor: "#f0f0f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <HomeIcon sx={{ fontSize: 60, color: "#ccc" }} />
                        </Box>
                      )}

                      {/* Type Badge */}
                      <Chip
                        label={
                          flat.availabilityStatus && flat.is_rent == 0 ? "Available" : "Occupied"
                        }
                        size="small"
                        color={flat.availabilityStatus && flat.is_rent == 0 ? "success" : "error"}
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={
                          flat.allowedFor === "family"
                            ? "Family"
                            : flat.allowedFor === "bachelor"
                            ? "Bachelor"
                            : "Family & Bachelor"
                        }
                        size="small"
                        icon={
                          flat.allowedFor === "family" ? (
                            <FamilyRestroomIcon />
                          ) : (
                            <PersonIcon />
                          )
                        }
                        sx={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                          bgcolor:
                            flat.allowedFor === "family"
                              ? "#11998e"
                              : "#667eea",
                          color: "white",
                          fontWeight: 600,
                          "& .MuiChip-icon": { color: "white" },
                        }}
                      />
                    </Box>

                    {/* Content */}
                    <Box
                      sx={{
                        p: 2,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Rent */}
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          mb: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {flat.flatName}
                      </Typography>
                      <PropertyRatingBadge flatId={flat._id} />
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: "#11998e", mb: 1 }}
                      >
                        Rs. {Number(flat.flatRent || 0).toLocaleString()}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          /month
                        </Typography>
                      </Typography>

                      {/* Location */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 2,
                          minHeight: 45,
                        }}
                      >
                        <LocationOnIcon
                          sx={{
                            fontSize: 18,
                            color: "#666",
                            mr: 0.5,
                            mt: 0.2,
                          }}
                        />

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            lineHeight: 1.4,
                          }}
                        >
                          {flat.propertyAddress ||
                            `${flat.flatNumber}, ${flat.city}`}
                        </Typography>
                      </Box>

                      {/* Features */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                          <Typography variant="body2">
                            {flat.flatRooms} Bed
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BathtubIcon sx={{ fontSize: 18, mr: 0.5 }} />
                          <Typography variant="body2">
                            {flat.flatToilet} Bath
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <ApartmentIcon sx={{ fontSize: 18, mr: 0.5 }} />
                          <Typography variant="body2">
                            Floor {flat.flatFloor}
                          </Typography>
                        </Box>
                      </Box>

                      {/* One line for the number that actually decides it:
                          what walking in costs. The per-charge breakdown is in
                          the detail modal — this card is for shortlisting, not
                          for deciding, and every extra row here is one more
                          screen of scrolling on a phone. */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          bgcolor: "#f8f9fa",
                          borderRadius: 2,
                          px: 1.4,
                          py: 1,
                          mb: 1.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Move-in cost
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: "#11998e" }}>
                          Rs {moveInTotal(flat).toLocaleString()}
                        </Typography>
                      </Box>

                      {/* A single chip row, four chips at most. The rule-set
                          badge earns its place because strictness changes the
                          decision; everything else that needs reading rather
                          than scanning belongs in the modal. */}
                      {(() => {
                        const amenities = amenityList(flat);
                        const notice = leaseTerms(flat).find(
                          (term) => term.label === "Notice period"
                        );
                        const showNotice = !hasTerms(flat) && notice;
                        const amenitySlots = showNotice ? 1 : 2;

                        return (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.6,
                              mb: 2,
                            }}
                          >
                            {hasTerms(flat) ? (
                              // Already reads "Standard terms • 30 Days
                              // notice", so no separate notice chip.
                              <RulesBadge rules={flat.terms} />
                            ) : (
                              flat.is_property_settings && (
                                <Chip
                                  icon={
                                    <VerifiedUserIcon sx={{ fontSize: 14 }} />
                                  }
                                  label="Terms registered"
                                  size="small"
                                  sx={{
                                    bgcolor: "#eff6ff",
                                    color: "#1d4ed8",
                                    fontSize: "0.68rem",
                                    fontWeight: 600,
                                    "& .MuiChip-icon": { color: "#1d4ed8" },
                                  }}
                                />
                              )
                            )}

                            {showNotice && (
                              <Chip
                                icon={<EventIcon sx={{ fontSize: 14 }} />}
                                label={`${notice.value} notice`}
                                size="small"
                                sx={{
                                  bgcolor: "#f1f5f9",
                                  color: "#334155",
                                  fontSize: "0.68rem",
                                  fontWeight: 600,
                                  "& .MuiChip-icon": { color: "#64748b" },
                                }}
                              />
                            )}

                            {amenities.slice(0, amenitySlots).map((label) => (
                              <Chip
                                key={label}
                                label={label}
                                size="small"
                                sx={{
                                  bgcolor: "#e8f5f3",
                                  color: "#0d7a6e",
                                  fontSize: "0.68rem",
                                  fontWeight: 600,
                                }}
                              />
                            ))}

                            {amenities.length > amenitySlots && (
                              <Chip
                                label={`+${amenities.length - amenitySlots}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.68rem" }}
                              />
                            )}
                          </Box>
                        );
                      })()}

                      <Divider sx={{ mb: 2 }} />

                      {/* Actions */}
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          title="Download full report"
                          onClick={() =>
                            downloadPublicPropertyReport(flat, {
                              name: item.landlordName,
                              email: item.landlordEmail,
                            })
                          }
                          sx={{
                            border: "1px solid #11998e",
                            borderRadius: 1,
                            color: "#11998e",
                          }}
                        >
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          onClick={() =>
                            handleViewDetails(flat, {
                              name: item.landlordName,
                              email: item.landlordEmail,
                            })
                          }
                          sx={{ borderColor: "#11998e", color: "#11998e" }}
                        >
                          Details
                        </Button>
                        <Button
                          variant="contained"
                          fullWidth
                          size="small"
                          onClick={() => handleApplyNow(flat)}
                          sx={{
                            bgcolor: "#11998e",
                            "&:hover": { bgcolor: "#0d7a6e" },
                          }}
                        >
                          Apply Now
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })
          )}
        </Grid>
      </Container>

      {/* ============ PROPERTY DETAIL MODAL ============ */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedProperty && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Property Details
              </Typography>
              <IconButton onClick={() => setDetailModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {/* Images */}
              {(selectedProperty.images || []).length > 0 && (
                <Box sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
                  <ImageSlider {...sliderSettings}>
                    {selectedProperty.images.map((img, idx) => (
                      <Box key={idx}>
                        <CardMedia
                          component="img"
                          height="300"
                          image={img}
                          alt={`Property ${idx + 1}`}
                          sx={{ objectFit: "cover" }}
                        />
                      </Box>
                    ))}
                  </ImageSlider>
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={700}>
                  {selectedProperty.flatName}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {selectedProperty.flatNumber}
                </Typography>
              </Box>
              {/* Rent */}
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ color: "#11998e", mb: 2 }}
              >
                Rs. {Number(selectedProperty.flatRent || 0).toLocaleString()}
                <Typography
                  component="span"
                  variant="body1"
                  color="text.secondary"
                >
                  /month
                </Typography>
              </Typography>

              {/* Location */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 3,
                }}
              >
                <LocationOnIcon
                  sx={{
                    color: "#666",
                    mr: 1,
                    mt: 0.3,
                  }}
                />

                <Typography variant="body1">
                  {selectedProperty.propertyAddress}
                </Typography>
              </Box>
              {selectedProperty?.lat && selectedProperty?.lng && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                  >
                    Property Location
                  </Typography>

                  <MapContainer
                    center={[
                      Number(selectedProperty.lat),
                      Number(selectedProperty.lng),
                    ]}
                    zoom={15}
                    scrollWheelZoom={false}
                    style={{
                      height: 300,
                      width: "100%",
                      borderRadius: 12,
                    }}
                  >
                    <TileLayer
                      attribution="&copy; OpenStreetMap contributors"
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    <Marker
                      position={[
                        Number(selectedProperty.lat),
                        Number(selectedProperty.lng),
                      ]}
                    />
                  </MapContainer>

                  {/* <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Lat: {Number(selectedProperty.lat).toFixed(6)} | Lng:{" "}
                    {Number(selectedProperty.lng).toFixed(6)}
                  </Typography> */}
                </Box>
              )}

              {/* Features Grid */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f5f7fa" }}>
                    <BedIcon sx={{ color: "#11998e", mb: 1 }} />
                    <Typography variant="h6">
                      {selectedProperty.flatRooms}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Bedrooms
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f5f7fa" }}>
                    <BathtubIcon sx={{ color: "#11998e", mb: 1 }} />
                    <Typography variant="h6">
                      {selectedProperty.flatToilet}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Bathrooms
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      bgcolor: "#f5f7fa",
                    }}
                  >
                    <ApartmentIcon
                      sx={{
                        color: "#11998e",
                        mb: 1,
                      }}
                    />

                    <Typography variant="h6">
                      {selectedProperty.flatFloor}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Floor
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      bgcolor: "#f5f7fa",
                    }}
                  >
                    <GroupsIcon
                      sx={{
                        color: "#11998e",
                        mb: 1,
                      }}
                    />

                    <Typography variant="h6">
                      {selectedProperty.personAllowed}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Persons
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: "center", bgcolor: "#f5f7fa" }}>
                    {selectedProperty.allowedFor === "family" ? (
                      <FamilyRestroomIcon sx={{ color: "#11998e", mb: 1 }} />
                    ) : (
                      <PersonIcon sx={{ color: "#11998e", mb: 1 }} />
                    )}
                    <Typography
                      variant="h6"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {selectedProperty.allowedFor}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Type
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Amenities */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Amenities
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 3,
                }}
              >
                {selectedProperty.pool === "true" && (
                  <Chip label="Pool" color="primary" />
                )}

                {selectedProperty.elevator === "true" && (
                  <Chip label="Elevator" color="secondary" />
                )}

                {selectedProperty.wifi === "true" && (
                  <Chip label="WiFi" color="success" />
                )}

                {selectedProperty.petAllowed === "true" && (
                  <Chip label="Pets Allowed" color="warning" />
                )}

                {selectedProperty.parkingAvailable === "true" && (
                  <Chip label="Parking" color="info" />
                )}

                {selectedProperty.furnished === "true" && (
                  <Chip label="Furnished" />
                )}
              </Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Costs
              </Typography>

              <Paper sx={{ p: 2, mb: 3, bgcolor: "#f8f9fa" }} elevation={0}>
                <Grid container spacing={2}>
                  {moveInCosts(selectedProperty).map((cost) => (
                    <Grid item xs={6} sm={4} key={cost.label}>
                      <Typography variant="body2" color="text.secondary">
                        {cost.label}
                      </Typography>
                      <Typography fontWeight={600}>{cost.value}</Typography>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Move-in total (rent + advance + security)
                  </Typography>
                  <Typography fontWeight={800} sx={{ color: "#11998e" }}>
                    Rs {moveInTotal(selectedProperty).toLocaleString()}
                  </Typography>
                </Box>
              </Paper>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Lease Terms
              </Typography>

              {leaseTerms(selectedProperty).length > 0 ? (
                <Paper sx={{ p: 2, mb: 3, bgcolor: "#f8f9fa" }} elevation={0}>
                  <Grid container spacing={2}>
                    {leaseTerms(selectedProperty).map((term) => (
                      <Grid item xs={6} sm={4} key={term.label}>
                        <Typography variant="body2" color="text.secondary">
                          {term.label}
                        </Typography>
                        <Typography fontWeight={600}>{term.value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  The landlord has not published lease terms for this listing.
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <GavelIcon sx={{ fontSize: 19, color: "#11998e" }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Termination &amp; Violation Terms
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                {hasTerms(selectedProperty) ? (
                  <PropertyRulesPanel
                    rules={selectedProperty.terms}
                    accent="#11998e"
                  />
                ) : (
                  // The server may predate the `terms` field. Say a rule-set
                  // exists rather than wrongly claiming there is none.
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: selectedProperty.is_property_settings
                        ? "#bfdbfe"
                        : "#fed7aa",
                      bgcolor: selectedProperty.is_property_settings
                        ? "#eff6ff"
                        : "#fff7ed",
                    }}
                  >
                    <Typography fontWeight={700} sx={{ mb: 0.6 }}>
                      {selectedProperty.is_property_settings
                        ? "Registered rule-set"
                        : "No rule-set registered"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedProperty.is_property_settings
                        ? "This property has a registered rule-set covering notice periods, early-exit penalties, immediate-termination grounds and deposit-refund conditions. Sign in and open the property to read the full text before you apply."
                        : "The landlord has not registered a termination and violation rule-set for this property. Ask for the exit terms in writing before you commit."}
                    </Typography>
                  </Paper>
                )}
              </Box>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                House Rules
              </Typography>

              {houseRules(selectedProperty).length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 3,
                  }}
                >
                  {houseRules(selectedProperty).map((rule) => (
                    <Chip key={rule} label={rule} variant="outlined" />
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  No house rules were listed for this property.
                </Typography>
              )}

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Facilities
              </Typography>

              {amenityList(selectedProperty).length > 0 ? (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}
                >
                  {amenityList(selectedProperty).map((label) => (
                    <Chip
                      key={label}
                      label={label}
                      size="small"
                      sx={{
                        bgcolor: "#e8f5f3",
                        color: "#0d7a6e",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  No facilities listed.
                </Typography>
              )}

              {/* Description */}
              {selectedProperty.flatDescription && (
                <>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {selectedProperty.flatDescription}
                  </Typography>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() =>
                  downloadPublicPropertyReport(
                    selectedProperty,
                    selectedLandlord
                  )
                }
                sx={{
                  mr: "auto",
                  borderColor: "#11998e",
                  color: "#11998e",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Full Report
              </Button>
              <Button
                variant="outlined"
                onClick={() => setDetailModalOpen(false)}
                sx={{ borderColor: "#ccc", color: "#666" }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={() => handleApplyNow(selectedProperty)}
                sx={{ bgcolor: "#11998e", "&:hover": { bgcolor: "#0d7a6e" } }}
              >
                Login to Apply
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
